import { useState } from 'react'
import { ActivityIndicator, Button, CodeBlock, ModalShell, StatePanel } from './common'
import api from '../services/api'
import styles from './FormValidator.module.css'

function FormValidator({ form, organizationSlug, onClose }) {
  const [formData, setFormData] = useState({})
  const [validationResult, setValidationResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleValidate = async () => {
    setLoading(true)
    setError(null)
    setValidationResult(null)
    
    try {
      const response = await api.post(
        `/${organizationSlug}/forms/${form.id}/validate`,
        formData
      )
      setValidationResult(response.data)
    } catch (err) {
      if (err.response?.data?.details) {
        setError(err.response.data.details)
      } else {
        setError(err.response?.data?.error || 'Validation failed')
      }
    } finally {
      setLoading(false)
    }
  }

  const renderField = (name, property, uiSchema = {}) => {
    const widget = uiSchema['ui:widget']
    const options = uiSchema['ui:options'] || {}
    const value = formData[name] || ''
    
    const commonProps = {
      value,
      onChange: (e) => setFormData({ ...formData, [name]: e.target.value }),
      placeholder: options.placeholder || property.title || name
    }

    // Handle different field types
    if (property.type === 'boolean' || widget === 'checkbox') {
      return (
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={formData[name] || false}
            onChange={(e) => setFormData({ ...formData, [name]: e.target.checked })}
          />
          {property.title || name}
        </label>
      )
    }

    if (property.enum || widget === 'select' || widget === 'radio') {
      const enumValues = property.enum || []
      
      if (widget === 'radio') {
        return (
          <div className={styles.radioGroup}>
            {enumValues.map(val => (
              <label key={val} className={styles.radioLabel}>
                <input
                  type="radio"
                  name={name}
                  value={val}
                  checked={formData[name] === val}
                  onChange={() => setFormData({ ...formData, [name]: val })}
                />
                {val}
              </label>
            ))}
          </div>
        )
      }
      
      return (
        <select {...commonProps}>
          <option value="">Select {property.title || name}</option>
          {enumValues.map(val => (
            <option key={val} value={val}>{val}</option>
          ))}
        </select>
      )
    }

    if (widget === 'textarea') {
      return (
        <textarea 
          {...commonProps}
          rows={options.rows || 4}
        />
      )
    }

    if (property.type === 'number' || property.type === 'integer') {
      return (
        <input
          type="number"
          {...commonProps}
          min={property.minimum}
          max={property.maximum}
          step={property.type === 'integer' ? 1 : 'any'}
        />
      )
    }

    if (property.format === 'date') {
      return (
        <input
          type="date"
          value={formData[name] || ''}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        />
      )
    }

    if (property.format === 'date-time' || widget === 'datetime') {
      return (
        <input
          type="datetime-local"
          value={formData[name] || ''}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        />
      )
    }

    if (property.format === 'email' || widget === 'email') {
      return <input type="email" {...commonProps} />
    }

    if (property.format === 'uri' || widget === 'url') {
      return <input type="url" {...commonProps} />
    }

    if (widget === 'password') {
      return <input type="password" {...commonProps} />
    }

    // Default to text input
    return (
      <input
        type="text"
        {...commonProps}
        minLength={property.minLength}
        maxLength={property.maxLength}
        pattern={property.pattern}
      />
    )
  }

  const renderObjectField = (name, property, uiSchema = {}) => {
    if (property.type === 'object' && property.properties) {
      return (
        <div className={styles.objectField}>
          <h4>{property.title || name}</h4>
          {Object.entries(property.properties).map(([subName, subProp]) => {
            const fullName = `${name}.${subName}`
            return (
              <div key={fullName} className={styles.formGroup}>
                <label>
                  {subProp.title || subName}
                  {form.schema?.required?.includes(fullName) && (
                    <span className={styles.required}>*</span>
                  )}
                </label>
                {renderField(fullName, subProp, uiSchema[subName])}
              </div>
            )
          })}
        </div>
      )
    }
    return null
  }

  return (
    <ModalShell
      open
      size="xl"
      title={`Test form: ${form.name}`}
      description="Validate sample data against the saved schema before you activate or reuse this form."
      onClose={onClose}
      footer={
        <>
          <div className={styles.jsonPreview}>
            <details>
              <summary>View current form data (JSON)</summary>
              <CodeBlock value={formData} label="Current form data" language="json" />
            </details>
          </div>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
        <div className={styles.content}>
          <div className={styles.formSection}>
            <h3>Form Data</h3>
            <p className={styles.formType}>Type: <strong>{form.form_type}</strong></p>
            
            <div className={styles.formFields}>
              {Object.entries(form.schema?.properties || {}).map(([name, property]) => {
                // Handle nested objects
                if (property.type === 'object' && property.properties) {
                  return renderObjectField(name, property, form.ui_schema?.[name])
                }
                
                return (
                  <div key={name} className={styles.formGroup}>
                    <label>
                      {property.title || name}
                      {form.schema?.required?.includes(name) && (
                        <span className={styles.required}>*</span>
                      )}
                    </label>
                    {property.description && (
                      <p className={styles.fieldDescription}>{property.description}</p>
                    )}
                    {renderField(name, property, form.ui_schema?.[name])}
                  </div>
                )
              })}
            </div>

            <Button
              type="button"
              className={styles.validateButton}
              onClick={handleValidate}
              disabled={loading}
            >
              {loading ? 'Validating...' : 'Validate Form Data'}
            </Button>
          </div>

          <div className={styles.resultSection}>
            <h3>Validation Result</h3>

            {loading ? (
              <ActivityIndicator status="running" label="Validating form data" detail="Schema checks and defaults are being applied." />
            ) : null}
            
            {error && (
              <StatePanel
                state="error"
                title="Validation failed"
                description="Review the validation output before activating this form."
                className={styles.resultPanel}
              >
                <CodeBlock value={error} label="Validation error" language="text" />
              </StatePanel>
            )}
            
            {validationResult && (
              <StatePanel
                state={validationResult.valid ? 'success' : 'error'}
                title={validationResult.valid ? 'Valid data' : 'Invalid data'}
                description={validationResult.valid ? 'Defaults and transformations are shown below.' : 'Fix the payload and validate again.'}
                className={styles.resultPanel}
              >
                {validationResult.data && (
                  <div className={styles.resultData}>
                    <h4>Processed Data (with defaults applied):</h4>
                    <CodeBlock value={validationResult.data} label="Processed form data" language="json" />
                  </div>
                )}
              </StatePanel>
            )}
            
            {!loading && !error && !validationResult && (
              <StatePanel
                state="empty"
                title="No validation run yet"
                description="Enter data and validate to inspect processed output and errors."
                compact
              />
            )}
          </div>
        </div>
    </ModalShell>
  )
}

export default FormValidator
